// src/App.js

import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Header from './components/Header';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import AiNewsPage from './pages/AiNewsPage';
import BackgroundWrapper from './components/BackgroundWrapper';
import LoadingSpinner from './components/LoadingSpinner';
import CustomCursor from './components/CustomCursor';

// Register GSAP's ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

// Animated Routes wrapper
const AnimatedRoutes = () => {
    const location = useLocation();

    return (
        <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/ai-news" element={<AiNewsPage />} />
            <Route path="/contact" element={<ContactPage />} />
        </Routes>
    );
};

const App = () => {
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Add Google Fonts
        const link = document.createElement('link');
        link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap';
        link.rel = 'stylesheet';
        document.head.appendChild(link);

        // Set a timer for simulating a loading screen
        const timer = setTimeout(() => setLoading(false), 2000);

        // Initial animation for page elements
        gsap.from('.app-container', {
            opacity: 0,
            y: 20,
            duration: 1.5,
            ease: 'power3.out',
            delay: 0.5,
        });

        // Enable smooth scrolling animations using ScrollTrigger
        ScrollTrigger.create({
            trigger: '.app-container',
            start: 'top top',
            end: 'bottom bottom',
            scrub: true,
        });

        return () => {
            clearTimeout(timer);
            document.head.removeChild(link);
        };
    }, []);

    return (
        <Router>
            <CustomCursor />
            <BackgroundWrapper>
                {loading ? (
                    <LoadingSpinner />
                ) : (
                    <div className="app-container">
                        <Header />
                        <main className="app-content">
                            <AnimatedRoutes />
                        </main>
                        <Footer />
                    </div>
                )}
            </BackgroundWrapper>

            <style jsx>{`
                :root {
                    --primary-color: #667eea;
                    --secondary-color: #764ba2;
                    --accent-color: #f093fb;
                    --success-color: #4facfe;
                    --warning-color: #f093fb;
                    --error-color: #ff6b6b;
                    --dark-100: #0f0f23;
                    --dark-200: #1a1a2e;
                    --dark-300: #16213e;
                    --dark-400: #0f3460;
                    --light-100: #ffffff;
                    --light-200: #f8fafc;
                    --light-300: #e2e8f0;
                    --light-400: #cbd5e1;
                    --gray-100: #a8b2d1;
                    --gray-200: #94a3b8;
                    --gray-300: #64748b;
                    --gray-400: #475569;
                }

                * {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }

                *::before,
                *::after {
                    margin: 0;
                    padding: 0;
                    box-sizing: border-box;
                }

                body {
                    font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                    background: linear-gradient(135deg, var(--dark-100) 0%, var(--dark-200) 50%, var(--dark-300) 100%);
                    color: var(--light-100);
                    line-height: 1.6;
                    overflow-x: hidden;
                    margin: 0;
                    padding: 0;
                    width: 100%;
                    min-height: 100vh;
                    cursor: none;
                }

                @media (pointer: coarse) {
                    body {
                        cursor: auto;
                    }
                }

                html {
                    scroll-behavior: smooth;
                    width: 100%;
                    height: 100%;
                    margin: 0;
                    padding: 0;
                }

                #root {
                    width: 100%;
                    min-height: 100vh;
                    margin: 0;
                    padding: 0;
                }

                .app-container {
                    min-height: 100vh;
                    width: 100%;
                    display: flex;
                    flex-direction: column;
                    background: linear-gradient(135deg, var(--dark-100) 0%, var(--dark-200) 50%, var(--dark-300) 100%);
                    margin: 0;
                    padding: 0;
                }

                .app-content {
                    flex: 1;
                    width: 100%;
                    padding-top: 80px; /* Height of the header */
                    position: relative;
                    animation: fadeIn 0.5s ease;
                    margin: 0;
                }

                @keyframes fadeIn {
                    from {
                        opacity: 0;
                        transform: translateY(20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                /* Add smooth scrolling to the entire page */
                html {
                    scroll-behavior: smooth;
                }

                /* Modern scrollbar styling */
                ::-webkit-scrollbar {
                    width: 10px;
                }

                ::-webkit-scrollbar-track {
                    background: var(--dark-200);
                }

                ::-webkit-scrollbar-thumb {
                    background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
                    border-radius: 5px;
                    border: 2px solid var(--dark-200);
                }

                ::-webkit-scrollbar-thumb:hover {
                    background: linear-gradient(135deg, var(--secondary-color) 0%, var(--accent-color) 100%);
                }

                /* Selection styling */
                ::selection {
                    background: var(--primary-color);
                    color: var(--light-100);
                }

                ::-moz-selection {
                    background: var(--primary-color);
                    color: var(--light-100);
                }

                /* Focus styles for accessibility */
                *:focus {
                    outline: 2px solid var(--primary-color);
                    outline-offset: 2px;
                }

                /* Typography improvements */
                h1, h2, h3, h4, h5, h6 {
                    font-weight: 600;
                    line-height: 1.2;
                    margin-bottom: 1rem;
                }

                h1 {
                    font-size: clamp(2rem, 5vw, 3.5rem);
                    font-weight: 700;
                }

                h2 {
                    font-size: clamp(1.5rem, 4vw, 2.5rem);
                    font-weight: 600;
                }

                h3 {
                    font-size: clamp(1.25rem, 3vw, 2rem);
                    font-weight: 600;
                }

                p {
                    font-size: 1.1rem;
                    line-height: 1.7;
                    color: var(--gray-100);
                }

                /* Link styling */
                a {
                    color: var(--primary-color);
                    text-decoration: none;
                    transition: all 0.3s ease;
                }

                a:hover {
                    color: var(--secondary-color);
                    text-decoration: underline;
                }

                /* Button styling */
                button {
                    font-family: 'Inter', sans-serif;
                    cursor: pointer;
                    transition: all 0.3s ease;
                }

                /* Responsive design */
                @media (max-width: 768px) {
                    .app-content {
                        padding-top: 70px;
                    }

                    h1 {
                        font-size: 2rem;
                    }

                    h2 {
                        font-size: 1.5rem;
                    }

                    h3 {
                        font-size: 1.25rem;
                    }

                    p {
                        font-size: 1rem;
                    }
                }

                @media (max-width: 480px) {
                    .app-content {
                        padding-top: 60px;
                    }
                }

                /* Animation utilities */
                .fade-in {
                    animation: fadeIn 0.8s ease forwards;
                }

                .slide-up {
                    animation: slideUp 0.8s ease forwards;
                }

                @keyframes slideUp {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }

                /* Glassmorphism utility */
                .glass {
                    background: rgba(255, 255, 255, 0.05);
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-radius: 20px;
                }

                /* Gradient text utility */
                .gradient-text {
                    background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
                    background-size: 200% 200%;
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    animation: gradientShift 3s ease infinite;
                }

                @keyframes gradientShift {
                    0% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                    100% { background-position: 0% 50%; }
                }
            `}</style>
        </Router>
    );
};

export default App;
